import React, { useState, useEffect, useContext } from 'react';
import { Input } from 'components/ui/input';
import { Button } from 'components/ui/button';
import {
  CollaboratorResponse,
  UserResponse,
  collabService,
} from '../../services/collaboratorApi';
import { BlogContext } from '../../BlogContext';
import { useDebounce } from '../../../../hooks/useDebounce';
import { Link, Loader2, Search, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { useSearchParams } from 'react-router-dom';
import BlogRemoveViewer from './BlogRemoveViewer';

interface AddNewUserProps {
  isOpen: boolean;
  onClose?: () => void;
}

const AddNewUser = ({
  isOpen,
  // onClose
}: AddNewUserProps) => {
  const [inputValue, setInputValue] = useState('');
  const [searchResults, setSearchResults] = useState<UserResponse[]>([]);
  const [collaborators, setCollaborators] = useState<CollaboratorResponse[]>(
    []
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserResponse | null>(null);
  const { blogContent } = useContext(BlogContext);
  const [searchParams] = useSearchParams();
  const blogId = searchParams.get('id');
  const debouncedSearch = useDebounce(inputValue, 500);

  useEffect(() => {
    if (debouncedSearch && blogId) {
      searchUsers(debouncedSearch);
    } else {
      setSearchResults([]);
    }
  }, [debouncedSearch]);

  useEffect(() => {
    if (blogId && isOpen) {
      fetchCollaborators();
    }
  }, [blogId, isOpen]);

  const searchUsers = async (query: string) => {
    if (query.length < 2) return;

    setIsLoading(true);
    try {
      const response = await collabService.searchUsers(query);
      // Filter out existing collaborators
      const filteredResults =
        response.data?.filter(
          (user) =>
            !collaborators.some(
              (collab) => collab.collaborator_user_username === user.username
            )
        ) || [];
      setSearchResults(filteredResults);
    } catch (error) {
      console.error('Error searching users:', error);
      toast.error('Failed to search users');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCollaborators = async () => {
    if (!blogId) return;

    try {
      const response = await collabService.getCollaborators(blogId);
      console.log(response);
      setCollaborators(response.data || []);
    } catch (error) {
      console.error('Error fetching collaborators:', error);
      toast.error('Failed to fetch collaborators');
    }
  };

  const handleInvite = async () => {
    if (!blogId || !inputValue || isSending) return;

    setIsSending(true);
    try {
      const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(inputValue);

      // Check if email is already invited
      if (
        isEmail &&
        collaborators.some((c) => c.collaborator_user_email === inputValue)
      ) {
        toast.error('This email has already been invited');
        return;
      }

      if (isEmail) {
        await collabService.inviteCollaborator(blogId, inputValue);
        toast.success(`Invitation sent to ${inputValue}`);
      } else {
        const user = searchResults.find((u) => u.username === inputValue);
        console.log(user);
        if (user?.username) {
          if (
            collaborators.some(
              (c) => c.collaborator_user_username === user.username
            )
          ) {
            toast.error('This user is already a collaborator');
            return;
          }
          await collabService.addCollaborator(blogId, user.username);
          toast.success(`${user.username} added as collaborator`);
        } else {
          toast.error('Please select a valid user or enter an email address');
          return;
        }
      }
      setInputValue('');
      setSearchResults([]);
      fetchCollaborators();
    } catch (error: any) {
      console.error('Error inviting user:', error);
      toast.error(
        error.response?.data?.message || 'Failed to add collaborator'
      );
    } finally {
      setIsSending(false);
    }
  };

  const handleRemove = async (collaboratorId: number, username: string) => {
    setSelectedUser({ username } as UserResponse);
    // BlogRemoveViewer will handle the actual removal
  };

  const handleCopyLink = () => {
    try {
      const currentUrl = window.location.href;
      navigator.clipboard.writeText(currentUrl);
      toast.success('Link copied to clipboard');
    } catch (error) {
      console.error(error);
      toast.error('Failed to copy link');
    }
  };

  return (
    <div className="relative">
      {isOpen && (
        <div className="absolute top-full mt-2  lg:w-[400px] lg:max-w-[500px] text-primary   rounded-lg bg-background shadow-lg z-40">
          <div className="p-4 border-b flex items-center justify-between">
            <span className="text-lg font-medium">SHARE VIEW ACCESS</span>
            <div
              className="flex items-center gap-2 cursor-pointer"
              onClick={handleCopyLink}
            >
              <Link className="h-4 w-4" />
              <span className="text-sm underline">Copy link</span>
            </div>
          </div>

          <div className="p-4 border-b">
            <h2 className="font-semibold text-xl mb-4">
              Share {blogContent?.title || 'Untitled'}
            </h2>
            <div className="flex gap-3">
              <div className="relative flex-1">
                <div className="relative">
                  <Input
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    placeholder="Username or email"
                    className="pr-8 text-text-primary"
                  />
                  {inputValue && (
                    <button
                      className="absolute right-2 top-1/2 -translate-y-1/2 "
                      onClick={() => {
                        setInputValue('');
                        setSearchResults([]);
                      }}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
                {isLoading && (
                  <div className="absolute right-2 top-1/2 -translate-y-1/2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                  </div>
                )}
                {searchResults.length > 0 && (
                  <div className="absolute w-full bg-white border rounded-md mt-1 shadow-lg max-h-48 overflow-y-auto">
                    {searchResults.map((user) => (
                      <div
                        key={user.user_id}
                        className="p-3 hover:bg-gray-50 cursor-pointer flex items-center gap-2"
                        onClick={() => {
                          setInputValue(user.username);
                          setSearchResults([]);
                        }}
                      >
                        <Search className="h-4 w-4 text-gray-400" />
                        <span>{user.username}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <Button
                variant="customSecondary"
                onClick={handleInvite}
                className="bg-secondary"
                disabled={!inputValue || isSending}
              >
                {isSending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  'Invite'
                )}
              </Button>
            </div>
          </div>

          <div className="max-h-[250px] overflow-y-auto">
            <div className="p-4 flex items-center justify-between font-medium">
              <span>You</span>
              <span>Owner</span>
            </div>

            {collaborators.map((collab) => (
              <div
                key={collab.id}
                className={`p-4 flex items-center justify-between border-t ${
                  collab.status === 'pending' ? 'opacity-70' : 'opacity-100'
                }`}
              >
                <span>
                  {collab.collaborator_user_username ||
                    collab.collaborator_user_email ||
                    'Unknown'}
                </span>
                <div className="flex items-center gap-4">
                  <button
                    className="text-primary underline text-sm"
                    onClick={() =>
                      handleRemove(
                        collab.id,
                        collab.collaborator_user_username || ''
                      )
                    }
                  >
                    Remove
                  </button>
                  <span className="text-sm">
                    {collab.status === 'pending' ? 'Pending' : 'Viewer'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {selectedUser && (
        <BlogRemoveViewer
          username={selectedUser.username}
          onConfirm={async () => {
            if (!blogId || !selectedUser) return;
            try {
              const collaborator = collaborators.find(
                (c) => c.collaborator_user_username === selectedUser.username
              );
              if (collaborator) {
                await collabService.removeCollaborator(blogId, collaborator.id);
                toast.success('Collaborator removed successfully');
                fetchCollaborators();
              }
            } catch (error) {
              console.error(error);
              toast.error('Failed to remove collaborator');
            }
            setSelectedUser(null);
          }}
          onCancel={() => setSelectedUser(null)}
        />
      )}
    </div>
  );
};

export default AddNewUser;
